import React, { useState } from 'react';
import { Button } from './atoms/Button';
import { StepIndicator } from './molecules/StepIndicator/StepIndicator';
import SelectionGroup from './molecules/SelectionGroup/SelectionGroup';
import CardSelector from './molecules/CardSelector/CardSelector';
import PriceDisplay from './molecules/PriceDisplay/PriceDisplay';
import { AccordionPanel } from './molecules/AccordionPanel/AccordionPanel';
import FileUpload from './molecules/FileUpload/FileUpload';
import { TemplateDownload } from './molecules/TemplateDownload/TemplateDownload';
import SelectProcessType from '../features/OrderFlow/steps/SelectProcessType';
import { useMarketInfo, formatPrice } from '../hooks/useMarketInfo';

const priceBreakdownData = [
  { label: 'インスタントコーヒー（フリーズドライ）', value: 80000 },
  { label: 'WAKOの豆', value: 10000 },
  { label: '銘柄名が入りますダミーです', value: 5000 },
  { label: '砂糖有り', value: 2000 },
  { label: '外箱', value: 3000 },
  { label: 'デザイン料', value: 30000 },
];

const mockTotalPrice = priceBreakdownData.reduce(
  (total, item) => total + (typeof item.value === 'number' ? item.value : 0),
  0,
);

export const ComponentPreview: React.FC = () => {
  const [radioValue, setRadioValue] = useState('one');
  const [selectionGroupValue, setSelectionGroupValue] = useState('wako');
  const [selectedCard, setSelectedCard] = useState('house-blend');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const marketInfo = useMarketInfo();

  const steps = [
    { sp: '加工形態の\n選択', pc: '加工形態の選択' },
    { sp: 'コーヒー豆を\n選択', pc: 'コーヒー豆を選択' },
    { sp: 'パッケージを\n選択', pc: 'パッケージを選択' },
    { sp: '発注数の\n選択', pc: '発注数の選択' },
    { sp: 'デザイン\n入稿', pc: 'デザイン入稿' },
  ];

  const cardSelectorOptions = [
    {
      value: 'house-blend',
      label: 'ハウスブレンド',
      priceInfo: `（${formatPrice(328, { showSymbol: true, showCurrency: false })}/100gあたり）`,
      description: 'ブラジルとコロンビアのみで味づくりを行い、あっさりと飲みやすく「毎日飲んでも飽きがこない」をテーマにした、味価格共にデイリーユース向けのブレンドコーヒーです。',
      image: 'https://via.placeholder.com/64', // Placeholder image
    },
    {
      value: 'dark-blend',
      label: 'ハウスダークブレンド',
      priceInfo: `（${formatPrice(328, { showSymbol: true, showCurrency: false })}/100gあたり）`,
      description: '香ばしい香りに、苦みを含んだ豊かなコクが特徴の深煎りのブレンドコーヒーです。',
      image: 'https://via.placeholder.com/64',
    },
  ];

  const selectionOptions = [
    {
      value: 'own',
      label: '自分の豆を使う',
      description: 'お客様が普段使用されているコーヒー豆をお送りください。',
      image: 'https://via.placeholder.com/64/ff0000', // Placeholder image
    },
    {
      value: 'wako',
      label: 'WAKOの豆を使う',
      description: 'WAKOで取り扱っておりますコーヒ豆からお選びください。',
      accordionContent: (
        <CardSelector
          title="銘柄を選択する"
          options={cardSelectorOptions}
          selectedValue={selectedCard}
          onChange={setSelectedCard}
        />
      ),
    },
  ];

  return (
    <>
      <div className="app" style={{ padding: '2rem', display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        <style>{`
          .component-group {
            display: flex;
            gap: 1rem;
            align-items: center;
            margin-bottom: 1rem;
            flex-wrap: wrap;
          }
          .component-group-column {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }
          h1, h2, h3 {
            border-bottom: 1px solid #eee;
            padding-bottom: 0.5rem;
            margin-bottom: 1rem;
          }
        `}</style>
        <h1>Component Preview</h1>

        {/* --- SelectProcessType --- */}
        <div>
          <h2>Select Process Type (Step 1)</h2>
          <SelectProcessType />
        </div>

        {/* --- Buttons --- */}
        <div>
          <h2>Buttons</h2>
          <div className="component-group">
            <Button variant="default">Default</Button>
            <Button variant="strong">Strong</Button>
            <Button variant="default" hasArrow>
              Default Arrow
            </Button>
            <Button variant="strong" hasArrow>
              Strong Arrow
            </Button>
          </div>
          <h3>States</h3>
          <div className="component-group">
            <Button variant="default" disabled>
              Default Disabled
            </Button>
            <Button variant="strong" disabled>
              Strong Disabled
            </Button>
          </div>
        </div>

        {/* --- StepIndicator --- */}
        <div>
          <h2>Step Indicator</h2>
          <div className="component-group-column">
            <h3>Step 1 Active</h3>
            <StepIndicator steps={steps} currentStep={1} />
            <h3>Step 3 Active</h3>
            <StepIndicator steps={steps} currentStep={3} />
            <h3>Step 5 Active</h3>
            <StepIndicator steps={steps} currentStep={5} />
          </div>
        </div>

        {/* --- SelectionGroup --- */}
        <div>
          <h2>Selection Group</h2>
          <div className="component-group-column">
            <SelectionGroup
              title="コーヒー豆を選択"
              name="coffeeBeans"
              options={selectionOptions}
              selectedValue={selectionGroupValue}
              onChange={setSelectionGroupValue}
              showQuestionIcon
            />
          </div>
        </div>

        {/* --- Figma Design Reproduction --- */}
        <div>
          <h2>Figma Design Reproduction</h2>
          <div className="component-group-column">

            <AccordionPanel title="インナーパッケージデザインテンプレートを選択">
              <TemplateDownload />
            </AccordionPanel>

            <AccordionPanel title="インナーパッケージデザインアップロード">
              <FileUpload 
                onFileSelect={setSelectedFile}
                selectedFile={selectedFile}
              />
            </AccordionPanel>
            
          </div>
        </div>
      </div>

      <div style={{ margin: '2rem 0' }}>
        <PriceDisplay price={mockTotalPrice} breakdown={priceBreakdownData} />
      </div>

      <div style={{ margin: '2rem 0' }}>
        <PriceDisplay price={120000} breakdown={priceBreakdownData.slice(0, 1)} isProvisional />
      </div>
    </>
  );
}; 